import { Stack } from "@fluentui/react";
import React, { useEffect, useMemo, useState } from "react";
import { AppType, DataPoints, getCitationFilePath } from "../../api";
import i18next from "../../i18n/i18n";
import styles from "./Answer.module.css";
import { parseAttachmentCitationToken } from "./AnswerParser";
import { resolvePdfImages } from "../../utils/pdfImageUtils";
import { normalizeManualCitationDisplayPath } from "../../utils/citationUtils";

interface Props {
    appType: AppType;
    citations: string[];
    dataPoints: DataPoints[];
    onCitationClicked: (filePath: string, citationIndex: number) => void;
    onAttachmentLinkClick?: (filename: string, page?: number) => void;
}

const buildCitationToken = (item: DataPoints) => `${item.uploaddir_field}/${item.sourcepage_field}`;

const getSourceCitationLabel = (citation: string, appType: AppType, displayIndex: number) => {
    if (appType === "FAQ") {
        return `${displayIndex}. ${citation.replace(/^[^/]*\//, "faq/")}`;
    }

    if (appType === "Manuals") {
        return `${displayIndex}. ${normalizeManualCitationDisplayPath(citation)}`;
    }

    if (citation.startsWith("product-manuals")) {
        return `${displayIndex}. ${normalizeManualCitationDisplayPath(citation)}`;
    }

    return `${displayIndex}. ${citation.replace(/^[^/]*\//, "faq/")}`;
};

const getCitationPreviewTitle = (citation: string, imageIndex: number, blobPath?: string) => {
    const pageMatch = citation.match(/#page=(\d+)/);
    if (pageMatch) {
        return `${i18next.t("answer.page")} ${pageMatch[1]} : ${i18next.t("answer.image")} ${imageIndex + 1}`;
    }
    if (blobPath) {
        return `${imageIndex + 1}`;
    }
    const fileName = citation.split("/").pop() ?? citation;
    return `${fileName} ${imageIndex + 1}`;
};

const getCitationImageAlt = (citation: string, imageIndex: number) => {
    const hashIndex = citation.indexOf("#");
    const pathPart = hashIndex >= 0 ? citation.slice(0, hashIndex) : citation;
    const fragment = hashIndex >= 0 ? citation.slice(hashIndex) : "";
    const fileName = pathPart.split("/").pop() ?? pathPart;
    return `${fileName}${fragment} : ${i18next.t("answer.image")} ${imageIndex + 1}`;
};

export const CitationList: React.FC<Props> = ({ appType, citations, dataPoints, onCitationClicked, onAttachmentLinkClick }) => {
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState<number | null>(null);

    const citationDataPointMap = useMemo(() => {
        const entries = new Map<string, DataPoints>();
        dataPoints.forEach(item => {
            entries.set(buildCitationToken(item), item);
            // Also index by sourcepage_field alone so citations emitted without
            // the uploaddir prefix (a valid form in AnswerParser) still resolve.
            if (item.sourcepage_field) {
                entries.set(item.sourcepage_field, item);
            }
        });
        return entries;
    }, [dataPoints]);

    const allCitationPreviewImages = useMemo(
        () =>
            citations.flatMap(citation => {
                const citationDataPoint = citationDataPointMap.get(citation);
                const pdfImages = resolvePdfImages(citationDataPoint);
                return pdfImages.map((image, imageIndex) => ({
                    citation,
                    imageBlobPath: image.blobPath,
                    imageIndex,
                    imageSrc: image.src
                }));
            }),
        [citations, citationDataPointMap]
    );

    useEffect(() => {
        if (selectedPreviewIndex === null) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                setSelectedPreviewIndex(prev => (prev !== null && prev > 0 ? prev - 1 : prev));
            } else if (e.key === "ArrowRight") {
                setSelectedPreviewIndex(prev => (prev !== null && prev < allCitationPreviewImages.length - 1 ? prev + 1 : prev));
            } else if (e.key === "Escape") {
                setSelectedPreviewIndex(null);
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [selectedPreviewIndex, allCitationPreviewImages.length]);

    if (!citations.length) return null;

    return (
        <>
            <Stack.Item>
                <Stack tokens={{ childrenGap: 8 }}>
                    {!!allCitationPreviewImages.length && (
                        <details className={styles.citationImageAccordion} open>
                            <summary className={styles.citationImageAccordionSummary}>
                                <span>{i18next.t("answer.supportingContent")}</span>
                                <span className={styles.citationImageAccordionCount}>{allCitationPreviewImages.length}</span>
                            </summary>
                            <div className={styles.citationImageList}>
                                {allCitationPreviewImages.map(({ citation, imageBlobPath, imageIndex, imageSrc }, previewIndex) => (
                                    <button
                                        key={`${citation}-image-${imageIndex + 1}`}
                                        type="button"
                                        className={styles.citationImageCard}
                                        onClick={() => setSelectedPreviewIndex(previewIndex)}
                                        aria-label={getCitationImageAlt(citation, imageIndex)}
                                        title={i18next.t("answer.supportingContent")}
                                    >
                                        <img src={imageSrc} alt={getCitationImageAlt(citation, imageIndex)} className={styles.citationImageThumb} />
                                        <span className={styles.citationImageMeta}>
                                            <span className={styles.citationImageTitle}>{getCitationPreviewTitle(citation, imageIndex, imageBlobPath)}</span>
                                            <span className={styles.citationImageSubtitle}>{`${i18next.t("answer.imagePreview")} ${previewIndex + 1}`}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </details>
                    )}
                    <span className={styles.citationLearnMore}>{i18next.t("answer.citations")}:</span>
                    {citations.map((citation, index) => {
                        const displayIndex = index + 1;
                        const attachmentCitation = parseAttachmentCitationToken(citation);
                        if (attachmentCitation) {
                            return (
                                <a
                                    key={`attachment-${displayIndex}`}
                                    className={styles.citation}
                                    title={attachmentCitation.displayText}
                                    onClick={() => onAttachmentLinkClick && onAttachmentLinkClick(attachmentCitation.fileName, attachmentCitation.page)}
                                >
                                    {`${displayIndex}. ${attachmentCitation.displayText}`}
                                </a>
                            );
                        }

                        const path = getCitationFilePath(citation);
                        const supportingCitationIndex = citations.slice(0, index + 1).filter(item => !parseAttachmentCitationToken(item)).length;

                        return (
                            <div key={`source-${citation}-${displayIndex}`} className={styles.citationItem}>
                                <a className={styles.citation} title={citation} onClick={() => onCitationClicked(path, supportingCitationIndex)}>
                                    {getSourceCitationLabel(citation, appType, displayIndex)}
                                </a>
                            </div>
                        );
                    })}
                </Stack>
            </Stack.Item>
            {selectedPreviewIndex !== null &&
                (() => {
                    const current = allCitationPreviewImages[selectedPreviewIndex];
                    const hasPrev = selectedPreviewIndex > 0;
                    const hasNext = selectedPreviewIndex < allCitationPreviewImages.length - 1;
                    return (
                        <div className={styles.citationImageOverlay} role="dialog" aria-modal="true" onClick={() => setSelectedPreviewIndex(null)}>
                            <button
                                type="button"
                                className={`${styles.citationImageNavButton} ${styles.citationImagePrevButton}${!hasPrev ? ` ${styles.citationImageNavButtonHidden}` : ""}`}
                                onClick={e => {
                                    e.stopPropagation();
                                    setSelectedPreviewIndex(selectedPreviewIndex - 1);
                                }}
                                aria-label={i18next.t("answer.previousImageAriaLabel", { defaultValue: "Previous image" })}
                                title={i18next.t("answer.previousImageAriaLabel", { defaultValue: "Previous image" })}
                                disabled={!hasPrev}
                                aria-hidden={!hasPrev}
                                tabIndex={!hasPrev ? -1 : undefined}
                            >
                                &#8249;
                            </button>
                            <div className={styles.citationImageDialog} onClick={event => event.stopPropagation()}>
                                <button
                                    type="button"
                                    className={styles.citationImageCloseButton}
                                    onClick={() => setSelectedPreviewIndex(null)}
                                    aria-label={i18next.t("answer.closeImagePreviewAriaLabel", { defaultValue: "Close image preview" })}
                                >
                                    &#x2715;
                                </button>
                                <img
                                    className={styles.citationImageFull}
                                    src={current.imageSrc}
                                    alt={i18next.t("answer.citationImagePreviewAlt", { defaultValue: "PDF image preview" })}
                                />
                                <span className={styles.citationImageCounter}>
                                    {selectedPreviewIndex + 1} / {allCitationPreviewImages.length}
                                </span>
                            </div>
                            <button
                                type="button"
                                className={`${styles.citationImageNavButton} ${styles.citationImageNextButton}${!hasNext ? ` ${styles.citationImageNavButtonHidden}` : ""}`}
                                onClick={e => {
                                    e.stopPropagation();
                                    setSelectedPreviewIndex(selectedPreviewIndex + 1);
                                }}
                                aria-label={i18next.t("answer.nextImageAriaLabel", { defaultValue: "Next image" })}
                                title={i18next.t("answer.nextImageAriaLabel", { defaultValue: "Next image" })}
                                disabled={!hasNext}
                                aria-hidden={!hasNext}
                                tabIndex={!hasNext ? -1 : undefined}
                            >
                                &#8250;
                            </button>
                        </div>
                    );
                })()}
        </>
    );
};
